<script lang="ts">
  import { tick } from 'svelte';
  import Scoreboard from './components/Scoreboard.svelte';
  import ToastRegion from './components/ToastRegion.svelte';
  import { toasts } from './components/toast-queue.svelte';
  import type { Card as CardData } from './lib/cards';
  import { heldNewCards } from './lib/progress';
  import { documentTitle, parseHash, routeTitle, type Route } from './lib/router';
  import { trackView } from './lib/telemetry';
  import Card from './routes/Card.svelte';
  import Drill from './routes/Drill.svelte';
  import Home from './routes/Home.svelte';
  import Keys from './routes/Keys.svelte';
  import Lab from './routes/Lab.svelte';
  import Lesson from './routes/Lesson.svelte';
  import Lessons, { type LessonGroup } from './routes/Lessons.svelte';
  import Meter from './routes/Meter.svelte';
  import Problem from './routes/Problem.svelte';
  import { returnToProblem } from './components/problem/return-to.svelte';
  import type { DraftStore } from './components/problem/types';
  import SessionDone from './routes/SessionDone.svelte';
  import Settings from './routes/Settings.svelte';
  import { STAGE_NAMES } from './routes/drill/home-stats';
  import { afterPaint } from './routes/drill/paint';
  import { appProgress } from './routes/drill/app-progress';
  import type { ProgressView } from './routes/drill/progress-view.svelte';
  import { createSession } from './routes/drill/session.svelte';
  import { noticeMessage } from './routes/drill/progress-view.svelte';
  import type { StoreNotice } from './lib/progress';
  import { catalog, lessons, problems, progress as store } from './services';

  interface Props {
    /** 進捗。既定はアプリ共有のもの（src/main.ts が mount の前に読み込む）。テストで差し替える */
    progress?: ProgressView;
  }

  let { progress = appProgress }: Props = $props();

  /**
   * セッション `#/session` は URL 直打ちのときだけ開く（ナビに導線は出さない。problem-first.md 追記）。
   * 中身は今日のキューのまま。レッスン未完了のカードは新規に出さない（lesson-format.md §8.1）。
   */
  function holdNew(): Set<string> {
    return heldNewCards(
      lessons.lessons.map((l) => ({ id: l.meta.id, cards: l.meta.cards, memos: l.meta.memos })),
      progress.snapshot.lessons,
    );
  }

  // svelte-ignore state_referenced_locally
  const session = createSession({ progress, cards: catalog.cards, holdNew });

  let route = $state<Route>(parseHash(typeof location === 'undefined' ? '' : location.hash));
  let confirmingAbort = $state(false);

  function syncFromLocation() {
    route = parseHash(location.hash);
  }

  /** replace = true は履歴を増やさない（転送・段階の切り替え） */
  function navigate(hash: string, replace = false) {
    if (replace) {
      history.replaceState(history.state, '', hash);
      syncFromLocation();
    } else if (location.hash === hash) {
      syncFromLocation();
    } else {
      location.hash = hash;
    }
  }

  function findCard(id: string): CardData | undefined {
    return catalog.byId.get(id) ?? catalog.byId.get(catalog.formerIdMap.get(id) ?? '');
  }

  /** Esc・「中断」: 確認ダイアログを開く（layout-and-navigation.md §4.2） */
  function requestAbort() {
    confirmingAbort = true;
  }

  function confirmAbort() {
    confirmingAbort = false;
    navigate('#/');
    toasts.show({ message: '中断しました。' });
  }

  /** レッスン一覧（参照用）。読了状態は出さない */
  const lessonGroups: LessonGroup[] = catalog.units.map((u) => ({
    unit: u.unit,
    title: `Stage ${u.stage} ${STAGE_NAMES[u.stage] ?? ''}`.trim(),
    useCase: u.useCase,
    lessons: (lessons.units.find((x) => x.unit === u.unit)?.lessonIds ?? [])
      .map((id) => lessons.byId.get(id))
      .filter((l) => l !== undefined)
      .map((l) => ({ id: l.meta.id, title: l.meta.title })),
  }));
  const sessionBar = $derived(
    route.name === 'session' && route.index !== undefined
      ? { index: route.index, total: session.total }
      : undefined,
  );
  const singleCard = $derived(route.name === 'card' ? findCard(route.cardId) : undefined);
  const lessonDoc = $derived(
    route.name === 'lesson' ? lessons.byId.get(route.lessonId) : undefined,
  );
  const problemDoc = $derived(
    route.name === 'problem' ? problems.byId.get(route.problemId) : undefined,
  );

  function showNotices(notices: readonly StoreNotice[]) {
    for (const n of notices) toasts.show(noticeMessage(n));
  }

  /** 問題の書きかけ（problem-format.md §8。debounce は Workbench） */
  const problemDrafts: DraftStore = {
    load: (key) => store.loadProblemDraft(key),
    save: (key, code, initial) => showNotices(store.saveProblemDraft(key, code, initial)),
    clear: (key) => showNotices(store.clearProblemDraft(key)),
  };

  // ---- 転送（存在しない画面・未開始のセッション） ----
  $effect(() => {
    const r = route;
    if (r.name === 'not-found') {
      navigate('#/', true);
      toasts.show({ message: 'このページは見つかりませんでした。ホームに戻りました。' });
      return;
    }
    if (r.name === 'card' && !findCard(r.cardId)) {
      navigate('#/', true);
      toasts.show({ message: 'このカードは見つかりませんでした。ホームに戻りました。' });
      return;
    }
    if (r.name === 'lesson' && !lessons.byId.has(r.lessonId)) {
      navigate('#/', true);
      toasts.show({ message: 'このレッスンは見つかりませんでした。ホームに戻りました。' });
      return;
    }
    if (r.name === 'problem' && !problems.byId.has(r.problemId)) {
      navigate('#/', true);
      toasts.show({ message: 'この問題は見つかりませんでした。ホームに戻りました。' });
      return;
    }
    // 「問題に戻る」は問題からレッスンへ移ったときだけ（problem-format.md §6.3）
    if (r.name !== 'lesson' && r.name !== 'problem') returnToProblem.clear();
    if (r.name !== 'session') confirmingAbort = false;
    if (r.name === 'session') {
      if (!session.started) session.start();
      if (!session.started) {
        navigate('#/', true);
        toasts.show({ message: '今出せるカードはありません。' });
        return;
      }
      if (r.index === undefined) {
        const first = session.firstUnanswered();
        navigate(first === null ? '#/session/done' : `#/session/${first}`, true);
      } else if (r.index > session.total) {
        navigate('#/session/done', true);
      }
    }
    if (r.name === 'session-done' && !session.started) navigate('#/', true);
  });

  /** Faro の view 名（observability.md §6.1）。カード ID は入れずテンプレートにする */
  function viewTemplate(r: Route): string | null {
    switch (r.name) {
      case 'home':
        return '/';
      case 'session':
        return r.index === undefined ? null : '/session/:index';
      case 'session-done':
        return '/session/done';
      case 'card':
        return '/card/:cardId';
      case 'lessons':
        return '/lessons';
      case 'lesson':
        return '/lesson/:lessonId';
      case 'problem':
        return '/problem/:problemId';
      case 'lab':
        return r.kind === undefined ? '/lab' : '/lab/:kind';
      case 'meter':
        return r.cardId === undefined ? '/meter' : '/meter/:cardId';
      case 'settings':
        return r.section === 'data' ? '/settings/data' : '/settings';
      case 'keys':
        return '/keys';
      case 'not-found':
        return null;
    }
  }

  // ---- 画面名とフォーカス（遷移をスクリーンリーダーに伝える）と view の計測 ----
  let mainEl: HTMLElement | undefined = $state();
  let lastHash = '';
  let lastView = '';
  $effect(() => {
    const r = route;
    if (r.name === 'lesson' && lessonDoc) document.title = documentTitle(lessonDoc.meta.title);
    else if (r.name === 'problem' && problemDoc)
      document.title = documentTitle(problemDoc.meta.title);
    else if (r.name !== 'session') document.title = documentTitle(routeTitle(r));
    const view = viewTemplate(r);
    if (view !== null && view !== lastView) {
      lastView = view;
      void trackView(view, afterPaint);
    }
    const hash = typeof location === 'undefined' ? '' : location.hash;
    const pathOnly = hash.split('?')[0] ?? '';
    if (pathOnly === lastHash) return;
    lastHash = pathOnly;
    // 節へのディープリンクは Lesson が見出しへスクロールしてフォーカスする（problem-format.md §6.3）
    if (r.name === 'lesson' && r.section !== undefined) return;
    void tick().then(() => {
      window.scrollTo(0, 0);
      mainEl?.querySelector<HTMLElement>('h1')?.focus({ preventScroll: true });
    });
  });
</script>

<svelte:window onhashchange={syncFromLocation} />

<a
  class="skip-link"
  href="#main"
  onclick={(e) => {
    e.preventDefault();
    mainEl?.focus();
  }}>本文へ移動</a
>

<Scoreboard {route} session={sessionBar} onabort={requestAbort} />

<main id="main" bind:this={mainEl} tabindex="-1">
  {#if route.name === 'home'}
    <Home onnavigate={navigate} />
  {:else if route.name === 'session' && route.index !== undefined}
    <Drill
      {session}
      useCases={catalog.useCases}
      index={route.index}
      {confirmingAbort}
      onnavigate={navigate}
      onabort={requestAbort}
      onconfirmabort={confirmAbort}
      oncancelabort={() => (confirmingAbort = false)}
    />
  {:else if route.name === 'session-done'}
    <SessionDone {session} onhome={() => navigate('#/')} />
  {:else if route.name === 'card' && singleCard}
    <Card card={singleCard} useCases={catalog.useCases} onnavigate={navigate} />
  {:else if route.name === 'lessons'}
    <Lessons groups={lessonGroups} />
  {:else if route.name === 'lesson' && lessonDoc}
    {#key lessonDoc.meta.id}
      <Lesson
        doc={lessonDoc}
        {progress}
        {findCard}
        stage={catalog.units.find((u) => u.unit === lessonDoc.meta.unit)?.stage}
        section={route.section}
        returnTo={returnToProblem.problemId}
      />
    {/key}
  {:else if route.name === 'problem' && problemDoc}
    {#key problemDoc.meta.id}
      <Problem doc={problemDoc} drafts={problemDrafts} {findCard} />
    {/key}
  {:else if route.name === 'lab'}
    <Lab kind={route.kind} step={route.step} onnavigate={navigate} />
  {:else if route.name === 'meter'}
    <Meter cardId={route.cardId} n={route.n} depth={route.depth} />
  {:else if route.name === 'settings'}
    <Settings section={route.section} {progress} />
  {:else if route.name === 'keys'}
    <Keys />
  {/if}
</main>

<ToastRegion />

<style>
  .skip-link {
    position: absolute;
    left: var(--space-2);
    top: -3rem;
    z-index: calc(var(--z-scoreboard) + 1);
    background: var(--surface);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
  }
  .skip-link:focus {
    top: var(--space-2);
  }
  main:focus {
    outline: none;
  }
  :global(.page) {
    max-width: var(--content-max);
    margin: 0 auto;
    padding: var(--space-6) var(--space-4) var(--space-8);
  }
  :global(.page-drill) {
    max-width: var(--drill-max);
  }
  :global(.page-narrow) {
    max-width: var(--drill-max);
  }
  @media (max-width: 760px) {
    :global(.page) {
      padding-top: var(--space-5);
    }
  }
</style>
